import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "customerId",
    "selectedName",
    "customerList",
    "searchInput",
    "customerItem",
    "dropdownContainer",
    "customerInfo",
    "customerName",
    "customerInitial",
    "phoneCount",
    "phoneSection",
    "phoneList",
    "agentLanguage",
    "customerLanguage",
    "submitButton",
    "form",
    "callStatus",
    "statusName"
  ];

  connect() {
    // If customer is pre-selected (e.g. from "Call customer" button), load their data
    if (this.hasCustomerIdTarget && this.customerIdTarget.value) {
      this.loadCustomerData();
    }
    
    // Close dropdown when clicking outside
    this.boundHandleClickOutside = (e) => this.handleClickOutside(e);
    document.addEventListener("click", this.boundHandleClickOutside);
  }

  disconnect() {
    if (this.boundHandleClickOutside) {
      document.removeEventListener("click", this.boundHandleClickOutside);
    }
  }

  // Toggle dropdown visibility
  toggle(event) {
    event.stopPropagation();
    if (this.hasCustomerListTarget) {
      this.customerListTarget.classList.toggle("hidden");
    }
  }

  // Handle clicks outside dropdown
  handleClickOutside(event) {
    if (this.hasDropdownContainerTarget && 
        !this.dropdownContainerTarget.contains(event.target) &&
        this.hasCustomerListTarget &&
        !this.customerListTarget.classList.contains("hidden")) {
      this.customerListTarget.classList.add("hidden");
    }
  }

  // Filter customers by search term
  filter(event) {
    const searchTerm = event.target.value.toLowerCase();
    const items = this.customerItemTargets;

    items.forEach(item => {
      const name = item.dataset.name?.toLowerCase() || "";
      const email = item.textContent.toLowerCase() || "";
      const matches = name.includes(searchTerm) || email.includes(searchTerm);
      item.classList.toggle("hidden", !matches);
    });
  }

  // Select a customer from the dropdown
  select(event) {
    const item = event.currentTarget;
    const customerId = item.dataset.id;
    const customerName = item.dataset.name;
    const customerLanguage = item.dataset.language || "";
    
    // Update hidden field and display
    if (this.hasCustomerIdTarget) {
      this.customerIdTarget.value = customerId;
    }
    if (this.hasSelectedNameTarget) {
      this.selectedNameTarget.textContent = customerName;
      this.selectedNameTarget.classList.remove("text-gray-500", "dark:text-gray-400");
    }
    
    // Close dropdown
    if (this.hasCustomerListTarget) {
      this.customerListTarget.classList.add("hidden");
    }
    
    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = "";
      // Reset filter to show all items
      this.customerItemTargets.forEach(item => {
        item.classList.remove("hidden");
      });
    }
    
    // Try to use phone numbers from data attribute first
    try {
      const phonesJson = item.dataset.phones;
      if (phonesJson) {
        const phoneNumbers = JSON.parse(phonesJson);
        if (phoneNumbers && phoneNumbers.length > 0) {
          // Display customer info and phone numbers from data
          this.displayCustomerInfo({ full_name: customerName, id: customerId });
          this.phoneCountTarget.textContent = `${phoneNumbers.length} phone number${phoneNumbers.length !== 1 ? "s" : ""}`;
          this.renderPhoneNumbers(phoneNumbers);
          this.phoneSectionTarget.classList.remove("hidden");
          this.updateLanguages({ preferred_language: customerLanguage }, customerLanguage);
          this.enableSubmit();
          return;
        }
      }
    } catch (error) {
      console.warn("Could not parse phone numbers from data attribute:", error);
    }
    
    // Fallback: Load customer data from API
    this.loadCustomerDataForId(customerId, customerLanguage);
  }

  async loadCustomerData() {
    const customerId = this.hasCustomerIdTarget ? this.customerIdTarget.value : null;

    if (!customerId) {
      this.hideCustomerInfo();
      return;
    }
    
    await this.loadCustomerDataForId(customerId);
  }

  async loadCustomerDataForId(customerId, preferredLanguage = null) {
    if (!customerId) {
      this.hideCustomerInfo();
      return;
    }

    try {
      // Derive organization identifier + locale from current path:
      // /:organization_identifier/:locale/...
      const parts = window.location.pathname.split("/").filter(Boolean);
      const organizationIdentifier = parts[0];
      const locale = parts[1] || "en";

      const response = await fetch(
        `/${organizationIdentifier}/${locale}/customers/${customerId}.json`
      );
      const customer = await response.json();

      this.displayCustomerInfo(customer);
      this.loadPhoneNumbers(customer);
      this.updateLanguages(customer, preferredLanguage);
      this.enableSubmit();
    } catch (error) {
      console.error("Error loading customer:", error);
    }
  }

  displayCustomerInfo(customer) {
    const customerName = customer.full_name || customer.name || "Unknown";
    this.customerNameTarget.textContent = customerName;
    this.customerInitialTarget.textContent = customerName[0].toUpperCase();
    this.customerInfoTarget.classList.remove("hidden");
  }

  async loadPhoneNumbers(customer) {
    const customerId = customer.id;

    try {
      // Derive organization identifier + locale from current path
      const parts = window.location.pathname.split("/").filter(Boolean);
      const organizationIdentifier = parts[0];
      const locale = parts[1] || "en";

      const response = await fetch(
        `/${organizationIdentifier}/${locale}/customers/${customerId}/phone_numbers.json`
      );
      const phoneNumbers = await response.json();
      
      this.phoneCountTarget.textContent = `${phoneNumbers.length} phone number${phoneNumbers.length !== 1 ? "s" : ""}`;
      
      if (phoneNumbers.length > 0) {
        this.renderPhoneNumbers(phoneNumbers);
        this.phoneSectionTarget.classList.remove("hidden");
      } else {
        this.phoneListTarget.innerHTML = `
          <div class="py-6 text-center text-gray-500 dark:text-gray-400">
            <p class="text-sm">No phone numbers available for this customer</p>
          </div>
        `;
        this.phoneSectionTarget.classList.remove("hidden");
      }
    } catch (error) {
      console.error("Error loading phone numbers:", error);
      this.phoneListTarget.innerHTML = `
        <div class="py-6 text-center text-gray-500 dark:text-gray-400">
          <p class="text-sm">Error loading phone numbers</p>
        </div>
      `;
    }
  }

  renderPhoneNumbers(phoneNumbers) {
    this.phoneListTarget.innerHTML = phoneNumbers.map((phone, index) => `
      <label class="flex items-center p-4 rounded-xl border-2 transition-all cursor-pointer ${index === 0 ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50"}">
        <input type="radio" 
               name="customer_phone_number_id" 
               value="${phone.id}" 
               ${index === 0 ? "checked" : ""}
               data-action="change->new-call#updateRadioStyles"
               class="mt-0.5 text-gray-900 border-gray-300 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400" />
        <div class="flex-1 ml-3">
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${phone.e164}
          </p>
          ${phone.primary ? `
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Primary
            </p>
          ` : ""}
        </div>
      </label>
    `).join("");
  }

  updateRadioStyles(event) {
    const groupName = event.target.name;
    const radios = this.element.querySelectorAll(`input[name="${groupName}"]`);
    
    radios.forEach((radio) => {
      const label = radio.closest("label");
      if (radio.checked) {
        label.classList.remove("border-gray-200", "dark:border-gray-700", "hover:border-gray-300", "dark:hover:border-gray-600", "hover:bg-gray-50", "dark:hover:bg-gray-700/50", "dark:hover:bg-gray-800/60");
        label.classList.add("border-indigo-500", "bg-indigo-50", "dark:bg-indigo-900/20", "dark:border-indigo-500");
      } else {
        label.classList.remove("border-indigo-500", "bg-indigo-50", "dark:bg-indigo-900/20", "dark:border-indigo-500");
        label.classList.add("border-gray-200", "dark:border-gray-700", "hover:border-gray-300", "dark:hover:border-gray-600", "hover:bg-gray-50", "dark:hover:bg-gray-700/50");
      }
    });
  }

  submit(event) {
    if (this.submitting) {
      event.preventDefault();
      return;
    }

    this.submitting = true;

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.value = "Connecting...";
    }

    if (this.hasStatusNameTarget && this.hasCustomerNameTarget) {
      this.statusNameTarget.textContent = this.customerNameTarget.textContent;
    }

    if (this.hasCallStatusTarget) {
      this.callStatusTarget.classList.remove("hidden");
    }
  }

  updateLanguages(customer, preferredLanguage = null) {
    // Update customer language if they have a preferred language
    const language = preferredLanguage || customer.preferred_language;
    if (language && this.hasCustomerLanguageTarget) {
      this.customerLanguageTarget.value = language;
    }
  }

  hideCustomerInfo() {
    this.customerInfoTarget.classList.add("hidden");
    this.phoneSectionTarget.classList.add("hidden");
    this.disableSubmit();
  }

  enableSubmit() {
    this.submitButtonTarget.disabled = false;
  }

  disableSubmit() {
    this.submitButtonTarget.disabled = true;
  }
}
